import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { clearTokens } from "@/hooks/useAuth";
import * as adminApi from "@/api/admin";
import { AdminSidebar } from "./AdminSidebar";
import { MessagesView } from "./MessagesView";
import { QuestionnaireSubmissionsView } from "./QuestionnaireSubmissionsView";
import { RedirectsView } from "./RedirectsView";
import { PartnersView } from "./PartnersView";
import { PageVideosView } from "./PageVideosView";
import { NewsView } from "./NewsView";
import { NovaDealsView, NovaHeaderView } from "./NovaDealsView";
import { NovaFeaturedOfferView } from "./NovaFeaturedOfferView";
import { NovaPerksView } from "./NovaPerksView";
import { EmailSettingsView } from "./EmailSettingsView";
import { ContentView } from "./ContentView";
import { LandingPagesView } from "./LandingPageView";
import { LandingPageEditorView } from "./LandingPageEditorView";
import type { ActiveView, SaveStatus } from "./types";

export function AdminPage() {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<ActiveView>({ type: "messages" });
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [questionnaireCount, setQuestionnaireCount] = useState<number | null>(null);

  function handleLogout() {
    clearTokens();
    navigate("/admin/login");
  }

  function handleNavigate(view: ActiveView) {
    setActiveView(view);
    setSidebarOpen(false);
  }

  async function handleSave(slug: string, title: string, content: string) {
    setSaveStatus("saving");
    try {
      await adminApi.updatePageContent(slug, { title, content });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
    }
  }

  const activeLabel =
    activeView.type === "messages" ? "Messages" :
    activeView.type === "leads" ? "Leads" :
    activeView.type === "questionnaires" ? "Questionnaires" :
    activeView.type === "redirects" ? "Redirects" :
    activeView.type === "partners" ? "Partners" :
    activeView.type === "videos" ? "Page Videos" :
    activeView.type === "news" ? "News" :
    activeView.type === "landing-pages" ? "Landing Pages" :
    activeView.type === "landing-page-editor" ? "Edit Landing Page" :
    activeView.type === "nova-deals" ? "Nova Deals" :
    activeView.type === "nova-header" ? "Nova Header" :
    activeView.type === "nova-featured-offer" ? "Featured Offer" :
    activeView.type === "nova-perks" ? "Perks" :
    activeView.type === "email-settings"
      ? (activeView.kind === "leads" ? "Leads Email Settings" : "Messages Email Settings") :
    activeView.label;

  return (
    <div className="flex h-screen bg-white font-sans overflow-hidden">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — always visible on md+, slide-in overlay on mobile */}
      <div className={`
        fixed inset-y-0 left-0 z-30 h-full transition-transform duration-300 md:static md:translate-x-0 md:z-auto
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        <AdminSidebar
          activeView={activeView}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
        />
      </div>

      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-200 px-4 md:px-8 py-4 shrink-0 flex items-start gap-3">
          {/* Hamburger — mobile only */}
          <button
            className="md:hidden mt-0.5 shrink-0 p-1 rounded-md text-gray-500 hover:bg-gray-100"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-400">
              Site &rsaquo; <span className="text-gray-600">{activeLabel}</span>
            </p>
            <h1 className="text-xl font-semibold text-gray-900 mt-0.5">{activeLabel}</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              {activeView.type === "messages"
                ? "Contact form submissions from visitors."
                : activeView.type === "leads"
                ? "Interest form submissions from service pages."
                : activeView.type === "questionnaires"
                ? "Questionnaire submissions from landing pages" +
                  (questionnaireCount === null
                    ? ""
                    : ` · ${questionnaireCount} submission${questionnaireCount === 1 ? "" : "s"}`)
                : activeView.type === "redirects"
                ? "QR redirects stored in the backend and their active status."
                : activeView.type === "partners"
                ? "Add or remove partners shown on the website."
                : activeView.type === "videos"
                ? "Upload, replace or remove the video shown at the top of each service page."
                : activeView.type === "landing-pages"
                ? "Build standalone landing pages with custom sections and questionnaires."
                : activeView.type === "landing-page-editor"
                ? "Add sections, build a custom questionnaire, and publish when ready."
                : activeView.type === "nova-deals"
                ? "Manage the Nova x PADU deal partners shown in the carousel."
                : activeView.type === "nova-header"
                ? "Edit the headline and header image shown at the top of the Nova page."
                : activeView.type === "nova-featured-offer"
                ? "Edit the heading and image shown in the featured offer box."
                : activeView.type === "nova-perks"
                ? "Edit the text of the four cards. Icons stay fixed."
                : activeView.type === "email-settings"
                ? activeView.kind === "leads"
                  ? "Configure the mail server and where new leads (interest forms) are delivered."
                  : "Configure the mail server and where contact-form messages are delivered."
                : `Manage the ${activeLabel} page content shown to all visitors.`}
            </p>
          </div>
        </header>

        {activeView.type === "messages" || activeView.type === "leads" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className=" px-4 md:px-8 py-6">
              <MessagesView source={activeView.type === "leads" ? "interest" : "contact"} />
            </div>
          </div>
        ) : activeView.type === "questionnaires" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <QuestionnaireSubmissionsView onCountChange={setQuestionnaireCount} />
            </div>
          </div>
        ) : activeView.type === "redirects" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <RedirectsView />
            </div>
          </div>
        ) : activeView.type === "partners" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <PartnersView />
            </div>
          </div>
        ) : activeView.type === "videos" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <PageVideosView />
            </div>
          </div>
        ) : activeView.type === "news" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <NewsView />
            </div>
          </div>
        ) : activeView.type === "landing-pages" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <LandingPagesView
                onEdit={(slug) => handleNavigate({ type: "landing-page-editor", slug })}
              />
            </div>
          </div>
        ) : activeView.type === "landing-page-editor" ? (
          <LandingPageEditorView
            key={activeView.slug}
            slug={activeView.slug}
            onBack={() => handleNavigate({ type: "landing-pages" })}
          />
        ) : activeView.type === "nova-deals" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <NovaDealsView />
            </div>
          </div>
        ) : activeView.type === "nova-header" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <NovaHeaderView />
            </div>
          </div>
        ) : activeView.type === "nova-featured-offer" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <NovaFeaturedOfferView />
            </div>
          </div>
        ) : activeView.type === "nova-perks" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <NovaPerksView />
            </div>
          </div>
        ) : activeView.type === "email-settings" ? (
          <div className="flex-1 overflow-y-auto pb-24">
            <div className="px-4 md:px-8 py-6">
              <EmailSettingsView key={activeView.kind} kind={activeView.kind} />
            </div>
          </div>
        ) : activeView.type === "content" ? (
          <ContentView
            key={activeView.slug}
            slug={activeView.slug}
            label={activeView.label}
            saveStatus={saveStatus}
            onSaveStatusChange={setSaveStatus}
            onSave={handleSave}
          />
        ) : null}
      </main>
    </div>
  );
}
